import type { LucideIcon } from 'lucide-react';
import type { ComponentPropsWithoutRef } from 'react';

import { cn } from '@/lib/utils';

type DirectionalIconProps = Omit<ComponentPropsWithoutRef<LucideIcon>, 'ref'> & {
  /** A direction-carrying lucide icon: a chevron, an arrow, a caret. */
  icon: LucideIcon;
};

/**
 * Renders a direction-indicating icon that mirrors itself in RTL.
 *
 * Icons that carry direction (back arrows, next chevrons, flow arrows) point
 * the wrong way in Arabic unless they are flipped. Purely decorative or
 * non-directional icons must NOT use this: a mirrored clock or download glyph
 * just looks broken.
 *
 * Implemented as a CSS mirror rather than by swapping left/right icon
 * components, because that is the pattern the marketing and docs surfaces
 * already shipped with (`rtl:-scale-x-100`), and because it needs no locale
 * hook, so it works unchanged in server components.
 *
 * @example
 * <DirectionalIcon icon={ChevronLeft} className="h-4 w-4" />
 */
export function DirectionalIcon({ icon: Icon, className, ...props }: DirectionalIconProps) {
  return <Icon aria-hidden className={cn('rtl:-scale-x-100', className)} {...props} />;
}
